import React, { useReducer, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import posed, { PoseGroup } from 'react-pose';
import { Elements } from 'react-stripe-elements';
import storage from 'web-storage';
import { useHistory } from 'react-router-dom';

import config from 'config';
import GlobalContext from 'site/context';
import Redirect from 'routing/redirect';
import { ContributeProvider } from './context';
import SEO from './seo';
import ProgressIndicator from './progress';
import SelectFundraiser from './select-fundraiser';
import SelectType from './select-type';
import SelectAmount from './select-amount';
import SelectTip from './select-tip';
import PaymentInfo from './payment-info';
import TimeoutMessage from './timeout-message';

const { localStorage } = storage();

const reducer = (state, action) => {
    switch (action.type) {
        case 'set-type':
            return { ...state, type: action.payload, amount: null, isBackpledging: false };

        case 'set-referrer':
            return { ...state, referrer: action.payload };

        case 'set-amount':
            return { ...state, amount: action.payload };

        case 'set-backpledge':
            return { ...state, isBackpledging: action.payload };

        case 'set-tip':
            return { ...state, tip: action.payload };

        case 'set-user-data':
            return { ...state, user: action.payload };

        case 'set-token':
            return { ...state, token: action.payload };

        case 'set-third-party-terms':
            return { ...state, thirdPartyTermsAccepted: action.payload };

        case 'submit':
            return { ...state, status: 'submitting', error: null };

        case 'error':
            return { ...state, status: 'error', error: action.payload };

        case 'complete':
            return { ...state, status: 'complete' };

        default:
            throw new Error(`No handler for action "${action.type}"`);
    }
};

const Step = posed.div({
    exit: { y: 50, opacity: 0, transition: { duration: 250 } },
    enter: { y: 0, opacity: 1, delay: 175, transition: { duration: 250 } },
});

const ContributePage = ({ step, campaign, fundraisers, referrer, onContribute }) => {
    const { user, showFlash } = useContext(GlobalContext);
    const history = useHistory();

    let initialStep = campaign.isDonationOnly ? 'fundraiser' : 'type';
    if (campaign.isDonationOnly && !fundraisers.length) initialStep = 'amount';

    const [state, dispatch] = useReducer(reducer, {
        status: 'default',
        referrer,
        type: campaign.isDonationOnly ? 'donation' : null,
        amount: null,
        isBackpledging: false,
        thirdPartyTermsAccepted: null,
        tip: null,
        user: null,
        token: null,
        error: null,
    });

    // Shorthand to make action calls less verbose
    const action = (type, payload) => dispatch({ type, payload });

    // Set relevant fields if user logs in (or is already)
    useEffect(() => {
        if (!user) return;

        dispatch({
            type: 'set-user-data',
            payload: {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                street1: user.address?.street1,
                street2: user.address?.street2,
                city: user.address?.city,
                stateProv: user.address?.stateProv,
                postalCode: user.address?.postalCode,
                country: user.address?.country,
            },
        });
    }, [user]);

    const gotoStep = nextStep => history.push(`/${campaign.slug}/contribute/${nextStep}`);

    const handleSubmit = async () => {
        // Fields not included:
        // thirdPartyTermsAccepted, matchDeclined, displayName
        const data = {
            campaign: campaign.id,
            type: state.type,
            tip: state.tip ?? 0,
            amount: state.amount,
            token: state.token,
            email: state.user.email,
            firstName: state.user.firstName,
            lastName: state.user.lastName,
        };

        // Pledge-specific data
        if (state.type === 'pledge') {
            data.performances = campaign.performances
                .filter(x => !x.isFinal || state.isBackpledging)
                .map(x => x.id);
        }

        // Referral-specific data
        if (state.referrer) {
            data.fundraiser = state.referrer;

            // Tracking IDs get set in LocalStorage when a user comes from a
            // referral link sent via email
            const trackingId = localStorage.get(`referral.${campaign.id}.${state.referrer}`);
            if (trackingId) data.referralTrackingId = trackingId;
        }

        // Address-specific data
        if (campaign.recipient.collectAddresses) {
            data.street1 = state.user.address.street1;
            data.street2 = state.user.address.street2;
            data.city = state.user.address.city;
            data.stateProv = state.user.address.stateProv;
            data.zip = state.user.address.postalCode;
        }

        // Third-party terms data
        if (state.thirdPartyTermsAccepted != null) {
            data.thirdPartyTermsAccepted = state.thirdPartyTermsAccepted;
        }

        const { completed } = await onContribute(data);

        // Taking longer than expected, need to notify the user
        if (!completed) return action('error', 'timeout');

        action('complete');
    };

    // Handle status changes
    useEffect(() => {
        if (state.status === 'submitting') {
            handleSubmit().catch(err => action('error', err?.response?.data ?? err));
            return;
        }

        if (state.status === 'error' && state.error !== 'timeout') {
            console.error('Error processing contribution', state.error);

            showFlash({
                type: 'danger',
                message: `There was a problem processing your ${state.type}. Your credit card was NOT charged. Please retry your contribution.`,
                duration: 5000,
            });
        }
    }, [state.status]);

    // Redirect back to the campaign, we're all done here!
    if (state.status === 'complete') {
        return <Redirect to={`/${campaign.slug}`} status={200} />;
    }

    // Round performanceRemaining to int
    campaign.stats.performanceRemaining = campaign.stats.performanceRemaining
        ? Math.round(campaign.stats.performanceRemaining)
        : 0;

    // Calculate total NOT including tip
    let subtotal = state.amount ?? 0;
    if (state.type === 'pledge' && state.amount) {
        subtotal = state.amount * campaign.stats.performanceRemaining;
        if (state.isBackpledging) subtotal += state.amount * campaign.stats.performanceActualTotal;
    }

    // Calculate total including tip
    const total = subtotal + (state.tip ?? 0);

    const steps = [
        { id: 'fundraiser', label: 'Give Credit' },
        { id: 'type', label: 'Type' },
        { id: 'amount', label: 'Amount' },
        { id: 'tip', label: 'Tip' },
        { id: 'payment', label: 'Payment' },
    ].filter(stepConfig => {
        // This only needs to happen in a separate step when a campaign is
        // donation-only and has fundraisers (which we calculated above)
        if (initialStep !== 'fundraiser' && stepConfig.id === 'fundraiser') return false;

        // Don't need to include the type step when donation-only
        if (campaign.isDonationOnly && stepConfig.id === 'type') return false;

        // Don't need to include the tip step if it's been disabled
        if (!campaign.effectiveEnableTips && stepConfig.id === 'tip') return false;

        // Also don't need to include the tip step if the amount is too low
        if (
            subtotal > 0 &&
            subtotal < config('/minimum_total_amount_for_tip') &&
            stepConfig.id === 'tip'
        ) {
            return false;
        }

        return true;
    });

    return (
        <>
            <ContributeProvider
                campaign={campaign}
                fundraisers={fundraisers}
                step={step}
                subtotal={subtotal}
                total={total}
                {...state}
            >
                <TimeoutMessage
                    show={state.error === 'timeout'}
                    onClose={() => action('complete')}
                />

                <div className="flex-grow relative xl:pr-48 xxxl:pr-0">
                    <SEO />
                    <Elements fonts={[{ cssSrc: config('/html/stylesheets')[0].href }]}>
                        <PoseGroup withParent={false}>
                            <Step key={step}>
                                {step === 'fundraiser' && (
                                    <SelectFundraiser
                                        onFundraiserChange={fundraiser =>
                                            action('set-referrer', fundraiser)
                                        }
                                        onContinue={() => gotoStep('amount')}
                                    />
                                )}
                                {step === 'type' && (
                                    <SelectType
                                        onTypeChange={type => action('set-type', type)}
                                        onFundraiserChange={fundraiser =>
                                            action('set-referrer', fundraiser)
                                        }
                                        onContinue={() => gotoStep('amount')}
                                    />
                                )}
                                {step === 'amount' && (
                                    <SelectAmount
                                        showPaymentRequest={!campaign.effectiveEnableTips}
                                        onAmountChange={amount => action('set-amount', amount)}
                                        onBackpledgeChange={bp => action('set-backpledge', bp)}
                                        onContinue={() => {
                                            const min = config('/minimum_total_amount_for_tip');
                                            const canTip =
                                                campaign.effectiveEnableTips && subtotal >= min;
                                            gotoStep(canTip ? 'tip' : 'payment');
                                        }}
                                    />
                                )}
                                {step === 'tip' && (
                                    <SelectTip
                                        onTipChange={amount => action('set-tip', amount)}
                                        onContinue={() => gotoStep('payment')}
                                    />
                                )}
                                {step === 'payment' && (
                                    <PaymentInfo
                                        isSubmitting={state.status === 'submitting'}
                                        onThirdPartyTermsChange={didAccept =>
                                            action('set-third-party-terms', didAccept)
                                        }
                                        onSubmit={data => {
                                            action('set-user-data', data.user);
                                            if (data.token) action('set-token', data.token);
                                            action('submit');
                                        }}
                                    />
                                )}
                            </Step>
                        </PoseGroup>
                    </Elements>
                    <div className="hidden xl:flex items-center absolute pin-r pin-y h-full mr-16">
                        <ProgressIndicator steps={steps} current={step} onClick={gotoStep} />
                    </div>
                </div>
            </ContributeProvider>
        </>
    );
};

ContributePage.propTypes = {
    step: PropTypes.oneOf(['fundraiser', 'type', 'amount', 'tip', 'payment']).isRequired,
    campaign: PropTypes.shape({
        id: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        isDonationOnly: PropTypes.bool.isRequired,
        effectiveEnableTips: PropTypes.bool.isRequired,
        recipient: PropTypes.shape({
            collectAddresses: PropTypes.bool.isRequired,
        }).isRequired,
        performances: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
            })
        ),
        stats: PropTypes.shape({
            performanceRemaining: PropTypes.number,
            performanceActualTotal: PropTypes.number,
        }).isRequired,
    }).isRequired,
    fundraisers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            user: PropTypes.shape({
                id: PropTypes.string,
                firstName: PropTypes.string,
                lastName: PropTypes.string,
                username: PropTypes.string,
            }),
        })
    ).isRequired,
    onContribute: PropTypes.func.isRequired,
    referrer: PropTypes.string,
};

ContributePage.defaultProps = {
    referrer: null,
};

export default ContributePage;
